<template>
  <div class="backlog-tasks">
    <div class="backlog-tasks-rows">
      <template v-if="backlogTasks">
        <div class="backlog-tasks-row"
             v-for="board in backlogTasks"
             v-bind:class="[{ active: board.active, 'active-filters': hasActiveFilters, 'empty': !board.tasks.length }, board.slug]">
          <div class="backlog-tasks-row-header"
               @dragover="board.active = true"
               @click="activateBoard(board)">
            <div class="backlog-tasks-row-title">
              {{ board.name }}
              <span class="backlog-tasks-row-title-counter">
              <template v-if="board.slug === 'completed'">
                Showing {{ board.tasks.length }} of
              </template>
              <template v-else-if="hasActiveFilters">
                {{ board.tasks.length }} of
              </template>
              {{ board.count }}
            </span>
            </div>
            <div class="backlog-tasks-row-arrow">
              <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="chevron-down"
                   class="svg-inline--fa fa-chevron-down fa-w-14" role="img" xmlns="http://www.w3.org/2000/svg"
                   viewBox="0 0 448 512">
                <path fill="currentColor" d="M207.029 381.476L12.686 187.132c-9.373-9.373-9.373-24.569 0-33.941l22.667-22.667c9.357-9.357
                    24.522-9.375 33.901-.04L224 284.505l154.745-154.021c9.379-9.335 24.544-9.317 33.901.04l22.667 22.667c9.373 9.373 9.373 24.569
                    0 33.941L240.971 381.476c-9.373 9.372-24.569 9.372-33.942 0z"></path>
              </svg>
            </div>
          </div>
          <div class="backlog-tasks-row-body" v-bind:class="{ 'no-items': !board.tasks.length }">
            <div class="no-items-text" v-if="!board.tasks.length">
              <template v-if="hasActiveFilters">
                <span>
                    No items in the list match the filters applied.
                    <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="inbox"
                         class="svg-inline--fa fa-inbox fa-w-18" role="img" xmlns="http://www.w3.org/2000/svg"
                         viewBox="0 0 576 512">
                      <path fill="currentColor" d="M567.938 243.908L462.25 85.374A48.003 48.003 0 0 0 422.311 64H153.689a48 48 0 0
                          0-39.938 21.374L8.062 243.908A47.994 47.994 0 0 0 0 270.533V400c0 26.51 21.49 48 48 48h480c26.51 0 48-21.49
                          48-48V270.533a47.994 47.994 0 0 0-8.062-26.625zM162.252 128h251.497l85.333 128H376l-32 64H232l-32-64H76.918l85.334-128z"></path>
                    </svg>
                  </span>
              </template>
              <template v-else>
                <template v-if="board.slug !== 'completed'">
                  <span>
                    Drag in here a task from the other lists, start organizing your workspace.
                    <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="magic"
                         class="svg-inline--fa fa-magic fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                      <path fill="currentColor" d="M224 96l16-32 32-16-32-16-16-32-16 32-32 16 32 16 16 32zM80 160l26.66-53.33L160
                          80l-53.34-26.67L80 0 53.34 53.33 0 80l53.34 26.67L80 160zm352 128l-26.66 53.33L352 368l53.34 26.67L432
                          448l26.66-53.33L512 368l-53.34-26.67L432 288zm70.62-193.77L417.77 9.38C411.53 3.12 403.34 0 395.15 0c-8.19
                          0-16.38 3.12-22.63 9.38L9.38 372.52c-12.5 12.5-12.5 32.76 0 45.25l84.85 84.85c6.25 6.25 14.44 9.37 22.62 9.37
                          8.19 0 16.38-3.12 22.63-9.37l363.14-363.15c12.5-12.48 12.5-32.75 0-45.24zM359.45 203.46l-50.91-50.91 86.6-86.6 50.91 50.91-86.6 86.6z"></path>
                    </svg>
                  </span>
                </template>
                <template v-else>
                  <span>
                    Nothing to show here yet. When a task changes the status to 'complete', it will appear here.
                    <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="inbox"
                         class="svg-inline--fa fa-inbox fa-w-18" role="img" xmlns="http://www.w3.org/2000/svg"
                         viewBox="0 0 576 512">
                      <path fill="currentColor" d="M567.938 243.908L462.25 85.374A48.003 48.003 0 0 0 422.311 64H153.689a48 48 0 0
                          0-39.938 21.374L8.062 243.908A47.994 47.994 0 0 0 0 270.533V400c0 26.51 21.49 48 48 48h480c26.51 0 48-21.49
                          48-48V270.533a47.994 47.994 0 0 0-8.062-26.625zM162.252 128h251.497l85.333 128H376l-32 64H232l-32-64H76.918l85.334-128z"></path>
                    </svg>
                  </span>
                </template>
              </template>
            </div>
            <draggable class="backlog-tasks-group"
                       tag="ul"
                       v-model="board.tasks"
                       v-bind="board.slug !== 'completed' ? dragOptions : completedDragOptions"
                       @start="drag = true"
                       @end="drag = false"
                       @change="handleChange($event, board)"
                       draggable=".task"
                       :group="board.slug !== 'completed' ? 'backlogTasks' : 'completedTasks'">
              <transition-group type="transition" :name="!drag ? 'flip-list' : null">
                <li class="backlog-tasks-group-item task"
                    v-for="task in board.tasks"
                    @click.prevent="goToTask(task.id)"
                    @dragstart="draggingItem(true)"
                    @dragend="draggingItem(false)"
                    @dragover="itemDragOver($event)"
                    :key="task.id">
                  <div class="backlog-tasks-group-item-data">
                    <span class="backlog-tasks-group-item-key" :class="{ 'line-through': task.status === 4 }">
                      {{ task.key }}
                    </span>
                      <span class="backlog-tasks-group-item-name">
                      {{ task.title }}
                    </span>
                    <div class="backlog-tasks-group-item-chapter"
                         v-if="board.slug !== 'completed' && task.chapter && getChapterById(task.chapter)">
                      <div class="backlog-tasks-group-item-chapter-progress">
                        <div class="backlog-tasks-group-item-chapter-image"
                             v-bind:style="{ backgroundImage: getChapterBackground(getChapterById(task.chapter)) }"></div>
                        <svg class="progress" width="130" height="130" viewBox="0 0 100 100">
                          <circle class="progress_meter" cx="50" cy="50" r="44" stroke-width="5"/>
                          <circle class="progress_value"
                                  :class="'filters_progress_value-' + getChapterById(task.chapter)['id']"
                                  cx="50" cy="50" r="44"
                                  v-if="getUserRole !== 'ta' && getUserRole !== 'tu'"
                                  stroke-width="5"/>
                        </svg>
                      </div>
                      <div class="backlog-tasks-group-item-chapter-name">{{ getChapterById(task.chapter)['name'] }}</div>
                    </div>
                  </div>
                  <div class="backlog-tasks-group-item-meta">
                    <div class="backlog-tasks-group-item-meta-mobile-chapter">
                      <div class="backlog-tasks-group-item-chapter"
                           v-if="board.slug !== 'completed' && task.chapter && getChapterById(task.chapter)">
                        <div class="backlog-tasks-group-item-chapter-progress">
                          <div class="backlog-tasks-group-item-chapter-image"
                               v-bind:style="{ backgroundImage: getChapterBackground(getChapterById(task.chapter)) }"></div>
                          <svg class="progress" width="130" height="130" viewBox="0 0 100 100">
                            <circle class="progress_meter" cx="50" cy="50" r="44" stroke-width="5"/>
                            <circle class="progress_value"
                                    :class="'filters_progress_value-' + getChapterById(task.chapter)['id']"
                                    cx="50" cy="50" r="44"
                                    v-if="getUserRole !== 'ta' && getUserRole !== 'tu'"
                                    stroke-width="5"/>
                          </svg>
                        </div>
                        <div class="backlog-tasks-group-item-chapter-name">{{ getChapterById(task.chapter)['name'] }}</div>
                      </div>
                    </div>
                    <div class="backlog-tasks-group-item-meta-properties">
                      <div class="backlog-tasks-group-item-meta-property recurrency" v-if="task.recurrent">
                        <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="sync-alt"
                             class="svg-inline--fa fa-sync-alt fa-w-16"
                             role="img" xmlns="http://www.w3.org/2000/svg"
                             viewBox="0 0 512 512">
                          <path fill="currentColor" d="M370.72 133.28C339.458 104.008 298.888 87.962 255.848 88c-77.458.068-144.328
                            53.178-162.791 126.85-1.344 5.363-6.122 9.15-11.651 9.15H24.103c-7.498 0-13.194-6.807-11.807-14.176C33.933
                            94.924 134.813 8 256 8c66.448 0 126.791 26.136 171.315 68.685L463.03 40.97C478.149 25.851 504 36.559 504
                            57.941V192c0 13.255-10.745 24-24 24H345.941c-21.382 0-32.09-25.851-16.971-40.971l41.75-41.749zM32 296h134.059c21.382
                            0 32.09 25.851 16.971 40.971l-41.75 41.75c31.262 29.273 71.835 45.319 114.876 45.28 77.418-.07 144.315-53.144
                            162.787-126.849 1.344-5.363 6.122-9.15 11.651-9.15h57.304c7.498 0 13.194 6.807 11.807 14.176C478.067 417.076 377.187
                            504 256 504c-66.448 0-126.791-26.136-171.315-68.685L48.97 471.03C33.851 486.149 8 475.441 8 454.059V320c0-13.255 10.745-24 24-24z"></path>
                        </svg>
                      </div>
                      <div class="backlog-tasks-group-item-meta-property mandatory" v-if="task.mandatory">
                        <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="exclamation-circle"
                             class="svg-inline--fa fa-exclamation-circle fa-w-16"
                             role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                          <path fill="currentColor" d="M504 256c0 136.997-111.043 248-248 248S8 392.997 8 256C8 119.083 119.043 8 256 8s248
                            111.083 248 248zm-248 50c-25.405 0-46 20.595-46 46s20.595 46 46 46 46-20.595 46-46-20.595-46-46-46zm-43.673-165.346l7.418
                            136c.347 6.364 5.609 11.346 11.982 11.346h48.546c6.373 0 11.635-4.982 11.982-11.346l7.418-136c.375-6.874-5.098-12.654-11.982-12.654h-63.383c-6.884
                            0-12.356 5.78-11.981 12.654z"></path>
                        </svg>
                      </div>
                    </div>
                    <div class="backlog-tasks-group-item-meta-user-info">
<!--                      <div class="backlog-tasks-group-item-meta-user priority"-->
<!--                           v-if="board.slug !== 'completed'"-->
<!--                           v-bind:class="['priority-' + taskPriorityDelta(task.score)]">-->
<!--                        <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="arrow-up"-->
<!--                             class="svg-inline&#45;&#45;fa fa-arrow-up fa-w-14" role="img"-->
<!--                             xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">-->
<!--                          <path fill="currentColor" d="M34.9 289.5l-22.2-22.2c-9.4-9.4-9.4-24.6 0-33.9L207 39c9.4-9.4 24.6-9.4 33.9 0l194.3 194.3c9.4 9.4 9.4 24.6-->
<!--                            0 33.9L413 289.4c-9.5 9.5-25 9.3-34.3-.4L264 168.6V456c0 13.3-10.7 24-24 24h-32c-13.3 0-24-10.7-24-24V168.6L69.2 289.1c-9.3 9.8-24.8 10-34.3.4z"></path>-->
<!--                        </svg>-->
<!--                        <span>{{ taskPriorityLabelByValue(taskPriorityDelta(task.score)) }}</span>-->
<!--                      </div>-->
                      <div class="backlog-tasks-group-item-meta-user status"
                           v-if="board.slug !== 'completed'"
                           v-bind:class="['status-' + task.status]">
                        <template v-if="task.status === 1">
                          <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="dot-circle"
                               class="svg-inline--fa fa-dot-circle fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                            <path fill="currentColor" d="M256 56c110.532 0 200 89.451 200 200 0 110.532-89.451 200-200 200-110.532 0-200-89.451-200-200
                                0-110.532 89.451-200 200-200m0-48C119.033 8 8 119.033 8 256s111.033 248 248 248 248-111.033 248-248S392.967 8 256 8zm0
                                168c-44.183 0-80 35.817-80 80s35.817 80 80 80 80-35.817 80-80-35.817-80-80-80z"></path>
                          </svg>
                        </template>
                        <template v-else-if="task.status === 2">
                          <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="spinner"
                               class="svg-inline--fa fa-spinner fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg"
                               viewBox="0 0 512 512">
                            <path fill="currentColor" d="M304 48c0 26.51-21.49 48-48 48s-48-21.49-48-48 21.49-48 48-48 48 21.49 48 48zm-48
                                368c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.49-48-48-48zm208-208c-26.51 0-48 21.49-48 48s21.49
                                48 48 48 48-21.49 48-48-21.49-48-48-48zM96 256c0-26.51-21.49-48-48-48S0 229.49 0 256s21.49 48 48 48 48-21.49
                                48-48zm12.922 99.078c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48c0-26.509-21.491-48-48-48zm294.156
                                0c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48c0-26.509-21.49-48-48-48zM108.922 60.922c-26.51 0-48
                                21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.491-48-48-48z"></path>
                          </svg>
                        </template>
                        <template v-else-if="task.status === 3">
                          <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="eye"
                               class="svg-inline--fa fa-eye fa-w-18" role="img" xmlns="http://www.w3.org/2000/svg"
                               viewBox="0 0 576 512">
                            <path fill="currentColor" d="M288 144a110.94 110.94 0 0 0-31.24 5 55.4 55.4 0 0 1 7.24 27 56 56 0 0 1-56 56 55.4
                                55.4 0 0 1-27-7.24A111.71 111.71 0 1 0 288 144zm284.52 97.4C518.29 135.59 410.93 64 288 64S57.68 135.64 3.48
                                241.41a32.35 32.35 0 0 0 0 29.19C57.71 376.41 165.07 448 288 448s230.32-71.64 284.52-177.41a32.35 32.35 0 0 0
                                0-29.19zM288 400c-98.65 0-189.09-55-237.93-144C98.91 167 189.34 112 288 112s189.09 55 237.93 144C477.1 345 386.66 400 288 400z"></path>
                          </svg>
                        </template>
                        <template v-else-if="task.status === 4">
                          <svg aria-hidden="true" focusable="false" data-prefix="far" data-icon="check-circle"
                               class="svg-inline--fa fa-check-circle fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg"
                               viewBox="0 0 512 512">
                            <path fill="currentColor" d="M256 8C119.033 8 8 119.033 8 256s111.033 248 248 248 248-111.033 248-248S392.967 8
                                256 8zm0 48c110.532 0 200 89.451 200 200 0 110.532-89.451 200-200 200-110.532 0-200-89.451-200-200 0-110.532
                                89.451-200 200-200m140.204 130.267l-22.536-22.718c-4.667-4.705-12.265-4.736-16.97-.068L215.346
                                303.697l-59.792-60.277c-4.667-4.705-12.265-4.736-16.97-.069l-22.719 22.536c-4.705 4.667-4.736 12.265-.068
                                16.971l90.781 91.516c4.667 4.705 12.265 4.736 16.97.068l172.589-171.204c4.704-4.668 4.734-12.266.067-16.971z"></path>
                          </svg>
                        </template>
                        <span class="status-text" v-if="getStatusById(task.status)">{{ getStatusById(task.status).name }}</span>
                      </div>
                      <div class="backlog-tasks-group-item-meta-user user-avatar">
                        <template v-if="taskAssigneeById(task.assignee)">
                          <span v-if="taskAssigneeById(task.assignee).image" v-bind:style="{ backgroundImage: 'url(\'' + imageBase + taskAssigneeById(task.assignee).image + '\')' }"></span>
                          <avatar v-else :username="taskAssigneeById(task.assignee).label" :size="32" color="#ffffff"></avatar>
                        </template>
                        <template v-else>
                          <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="user-circle"
                               class="svg-inline--fa fa-user-circle fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg"
                               viewBox="0 0 496 512">
                            <path fill="currentColor" d="M248 8C111 8 0 119 0 256s111 248 248 248 248-111 248-248S385 8 248 8zm0 96c48.6 0
                                88 39.4 88 88s-39.4 88-88 88-88-39.4-88-88 39.4-88 88-88zm0 344c-58.7 0-111.3-26.6-146.5-68.2 18.8-35.4
                                55.6-59.8 98.5-59.8 2.4 0 4.8.4 7.1 1.1 13 4.2 26.6 6.9 40.9 6.9 14.3 0 28-2.7 40.9-6.9 2.3-.7 4.7-1.1
                                7.1-1.1 42.9 0 79.7 24.4 98.5 59.8C359.3 421.4 306.7 448 248 448z"></path>
                          </svg>
                        </template>
                      </div>
                    </div>
                  </div>
                </li>
              </transition-group>
            </draggable>
            <div class="backlog-tasks-row-body-actions"
                 v-if="board.slug === 'completed' && board.tasks.length < board.count">
              <button>Show all completed tasks</button>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
  import api from '@/api'
  import draggable from 'vuedraggable'
  import {mapGetters} from 'vuex'
  import Avatar from 'vue-avatar'

  export default {
    name: 'backlog-tasks',
    props: {
      company: Number
    },
    data () {
      return {
        drag: false,
        completedTasksLimit: 3,
        backlogTasks: null
      }
    },
    methods: {
      handleChange (value, board) {
        if (value.hasOwnProperty('added')) {
          board.count++
        }
        if (value.hasOwnProperty('removed')) {
          board.count--
        }
        this.moveTask(board, value)
        // this.setChaptersProgress()
      },
      moveTask (board, value) {
        const backlogBoard = this.backlogTasks.find((item) => item.id === board.id)
        if (!backlogBoard) {
          return
        }
        const data = {
          targetBoard: backlogBoard.id,
          indexes: this.generateNewTasksOrder(backlogBoard),
          filtered: this.hasActiveFilters
        }
        if (value.hasOwnProperty('added')) {
          data['action'] = 'added'
          data['taskId'] = value.added.element.id
          data['newIndex'] = parseInt(value.added.newIndex)
        }
        if (value.hasOwnProperty('moved')) {
          data['action'] = 'moved'
          data['taskId'] = value.moved.element.id
          data['newIndex'] = parseInt(value.moved.newIndex)
        }
        if (value.hasOwnProperty('removed')) {
          data['action'] = 'removed'
          data['newIndex'] = parseInt(value.removed.newIndex)
        }
        if (this.company) {
          data['company'] = this.company
        }
        this.$http.post(`${api.URLS.task}/move`, data, api.headersAuthSettings).then(() => {}).catch((err) => {
          console.log(err)
        })
      },
      generateNewTasksOrder (board) {
        const order = []
        for (let i = 0; i < board.tasks.length; i++) {
          order.push(board.tasks[i]['id'])
        }
        return order
      },
      goToTask (taskId) {
        const routeName = this.isAdmin ? 'company-task' : 'task'
        const routeParams = {
          id: taskId
        }
        if (this.company) {
          routeParams['companyId'] = this.company
        }
        let props = this.$router.resolve({
          name: routeName,
          params: routeParams
        })
        this.$router.push(props.href)
      },
      activateBoard (board) {
        board.active = !board.active
      },
      getChapterById (id) {
        return this.taskChapters.find((item) => item.id === id)
      },
      getStatusById (id) {
        const status = this.taskStatuses.find((item) => item.id === id)
        return status || false
      },
      getChapterBackground (chapter) {
        if (this.selectedHightCont === 1) {
          return 'url(' + chapter.image_high + ')'
        } else {
          return 'url(' + chapter.image + ')'
        }
      },
      setChaptersProgress () {
        const self = this
        setTimeout(function () {
          self.taskChapters.forEach(function (chapter) {
            let score = chapter.score
            let selector = '.backlog-tasks .filters_progress_value-' + chapter.id
            self.setCompanyProgress(score, selector)
          })
        }, 1000)
      },
      setCompanyProgress (value, selector) {
        let RADIUS = 44
        let CIRCUMFERENCE = 2 * Math.PI * RADIUS
        let progressValue = document.querySelectorAll(selector)
        if (progressValue.length) {
          for (let i = 0; i < progressValue.length; i++) {
            let progress = value / 100
            if (progressValue[i]) {
              progressValue[i].style.opacity = 1
              progressValue[i].style.strokeDasharray = CIRCUMFERENCE
              progressValue[i].style.strokeDashoffset = CIRCUMFERENCE * (1 - progress)
            }
          }
        }
      },
      taskPriorityLabelByValue (value) {
        const priority = this.tasksPriorities.find((item) => item.value === value)
        return priority ? priority.label : ''
      },
      taskPriorityDelta (value) {
        let priorityDelta = 0
        if (value <= 33) {
          priorityDelta = 0
        } else if (value > 33 && value <= 66) {
          priorityDelta = 1
        } else if (value > 66 && value <= 100) {
          priorityDelta = 2
        }
        return priorityDelta
      },
      itemDragOver (event) {
        const board = event.target.closest('.backlog-tasks-row-body.no-items')
        if (board) {
          board.classList.add('drag-over')
        } else {
          const boards = document.querySelectorAll('.backlog-tasks-row-body.drag-over')
          for (let i = 0; i < boards.length; i++) {
            boards[i].classList.remove('drag-over')
          }
        }
      },
      getData () {
        const searchParams = this.searchParams
        let actionURL = `${api.URLS.task}/all/backlog`
        if (searchParams) {
          if (this.company) {
            actionURL += `?company=${this.company}&filters=${searchParams}`
          } else {
            actionURL += `?filters=${searchParams}`
          }
        } else if (this.company) {
          actionURL += `?company=${this.company}`
        }
        this.$http.get(`${actionURL}`, api.headersAuthSettings)
          .then((res) => {
            this.backlogTasks = res.body
            this.$store.commit('setBacklogTasks', res.body)
            // this.setChaptersProgress()
          })
          .catch((err) => {
            console.log(err)
          })
      },
      taskAssigneeById (id) {
        const assignee = this.taskAssigneeForSelect.find((item) => item.value === id)
        if (assignee && assignee.value) {
          return assignee
        }
        return null
      },
      draggingItem (state) {
        const header = document.querySelector('.app-header-main')
        if (state) {
          header.classList.add('absolute-positioned')
        } else {
          header.classList.remove('absolute-positioned')
        }
      }
    },
    computed: {
      ...mapGetters([
        'getUserRole',
        'tasksPriorities',
        'taskStatuses',
        'taskChapters',
        'taskAssigneeForSelect',
        'isAdmin'
      ]),
      dragOptions () {
        return {
          animation: 200,
          group: 'backlogTasks',
          disabled: false,
          delay: 200,
          delayOnTouchOnly: true,
          ghostClass: 'ghost'
        }
      },
      completedDragOptions () {
        return {
          animation: 200,
          group: 'backlogTasks',
          disabled: true,
          delay: 200,
          delayOnTouchOnly: true,
          ghostClass: 'ghost'
        }
      },
      hasActiveFilters () {
        let hasFilters = false
        if (
          this.$route.query.bt ||
          this.$route.query.bc ||
          this.$route.query.ba ||
          this.$route.query.status
        ) {
          hasFilters = true
        }
        return hasFilters
      },
      searchParams () {
        const filters = {}
        let empty = true
        if (this.$route.query.bt) {
          filters['text'] = decodeURIComponent(this.$route.query.bt)
          empty = false
        }
        if (this.$route.query.bc) {
          filters['chapters'] = decodeURIComponent(this.$route.query.bc).split(',')
          empty = false
        }
        if (this.$route.query.ba) {
          filters['assignee'] = decodeURIComponent(this.$route.query.ba).split(',')
          empty = false
        }
        if (this.$route.query.status) {
          filters['status'] = decodeURIComponent(this.$route.query.status).split(',')
          empty = false
        }
        if (empty) {
          return false
        }
        return encodeURIComponent(JSON.stringify(filters))
      },
      imageBase () {
        return api.serverUserImages
      }
    },
    components: {
      draggable,
      Avatar
    },
    watch: {
      '$route' (to, from) {
        if ((to.params.companyId && from.params.companyId && to.params.companyId !== from.params.companyId) || (to.hash !== from.hash && to.hash === '#backlog')) {
          this.getData()
        }
      },
      drag (value) {
        if (!value) {
          const boards = document.querySelectorAll('.backlog-tasks-row-body')
          for (let i = 0; i < boards.length; i++) {
            boards[i].classList.remove('drag-over')
          }
        }
      },
      taskChapters (value) {
        if (value) {
          // this.setChaptersProgress()
        }
      },
      searchParams () {
        this.getData()
      }
    },
    created () {
      this.getData()
    }
  }
</script>

<style src="@/assets/scss/components/tasks/backlog-tasks.scss" lang="scss" scoped />



// WEBPACK FOOTER //
// BacklogTasks.vue?6fe3b83e